import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BatteryFullIcon, CheckIcon, DropletsIcon, EyeIcon, RefreshCwIcon } from 'lucide-react';
import { ObjectionId, objectionChoices, RECOVERY_ID } from '../data/objections';
import { lenses, offers } from '../data/product';
import { CtaButton } from './CtaButton';
import { LifetimeSeal } from './LifetimeSeal';
import { useTrackOnView } from '../hooks/useTrackOnView';
import { trackCustom, packEvents } from '../utils/tracking';
import { requestOffer, PackId } from '../utils/offerBus';
interface ObjectionRecoveryProps {
  /** The objection the visitor picked. Nothing renders until they pick one. */
  active: ObjectionId | null;
  /** True once the visitor has entered the price path at least once. */
  priceUnlocked: boolean;
  /** Reopens the six-option selector. */
  onChooseAnother: () => void;
}
const headlineClass = 'grit-headline text-[clamp(2.25rem,8vw,4.5rem)] font-extrabold uppercase leading-[0.92] text-offwhite';
const subClass = 'mt-5 max-w-2xl text-[15px] leading-relaxed text-steel-300 sm:text-lg';
const statementClass = 'mt-6 border-l-4 border-grit-500 pl-4 font-display text-2xl font-bold uppercase leading-tight text-offwhite sm:text-3xl';
function Bullets({
  items
}: {
  items: string[];
}) {
  return <ul className="mt-6 grid gap-2.5" data-id="element-396" data-mp="29ydb3cvrjpyz">
      {items.map(line => <li key={line} className="flex items-start gap-2.5 text-[15px] font-medium text-offwhite" data-id="element-397" data-mp="2xmmr1y6apji7">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-398" data-mp="32kfyeujc4jk2" />
          {line}
        </li>)}
    </ul>;
}

/**
 * The compact close every funnel ends on: the three consumer packs, with the
 * one that answers this objection marked as recommended. Picking one preselects
 * it in the main offer section and drops the visitor there.
 */
function PackClose({
  recommended,
  cta
}: {
  recommended: PackId;
  cta: string;
}) {
  return <div className="mt-10 border-t border-white/10 pt-8" data-id="element-399" data-mp="3v7xv5ov9w5x9">
      <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-400" data-mp="3v5bl9ikwtebv">Recommended for you</p>
      <ul className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-401" data-mp="3m6zlapy5l46r">
        {offers.map(offer => {
        const isPick = offer.id === recommended;
        return <li key={offer.id} data-id="element-402" data-mp="3h7el6c78ypsg">
              <a href="#offer" data-buy-intent="true" onClick={() => {
            requestOffer(offer.id);
            trackCustom(packEvents[offer.id].addToCart);
            trackCustom('mini_funnel_cta_clicked');
          }} className={`flex h-full flex-col border p-4 transition-[border-color,background-color] duration-150 ease-grit focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${isPick ? 'border-grit-500 bg-grit-500/10 hover:bg-grit-500/15' : 'border-white/15 bg-ink-900 hover:border-white/35'}`} data-id="element-403" data-mp="3c8tm4g3qec5l">
                {offer.badge ? <span className="mb-2 self-start bg-grit-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-ink-950" data-id="element-404" data-mp="18z8gcijjmvht">
                    {offer.badge}
                  </span> : <span className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-steel-500" data-id="element-405" data-mp="1i0g2k28sc5vj">
                    {offer.name}
                  </span>}
                <span className="font-display text-2xl font-bold uppercase leading-none text-offwhite" data-id="element-406" data-mp="qav2fqy5ejbi">
                  {offer.units}
                </span>
                <span className="mt-2 font-display text-3xl font-extrabold leading-none text-offwhite" data-id="element-407" data-mp="z23r29ueijws">
                  {offer.price}
                </span>
                {offer.perUnit && <span className="mt-1 text-[12px] font-semibold text-steel-400" data-id="element-408" data-mp="6lt005yft5p4">{offer.perUnit}</span>}
              </a>
            </li>;
      })}
      </ul>

      <CtaButton offer={recommended} className="mt-6 w-full sm:w-auto" onClick={() => trackCustom('mini_funnel_cta_clicked')} data-id="element-409" data-mp="3jk91a6ywrotx">
        {cta}
      </CtaButton>
    </div>;
}
function BatteryFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-410" data-mp="3okn9pq4f90sh">
        Your shift doesn&apos;t end
        <br data-id="element-411" data-mp="675yglchiht9" />
        <span className="text-grit-500" data-id="element-412" data-mp="66w5qq021wjv">when one battery does.</span>
      </h2>
      <p className={subClass} data-id="element-413" data-mp="18umo52mpx8e">
        We are not going to pretend one X1 lasts an entire workday. One unit runs up to 14 hours on low and about 5
        on high, so if you burn high light all day, you rotate. That is exactly what Buy 2, Get 1 Free is for.
      </p>
      <p className={statementClass} data-id="element-414" data-mp="b4p9zdhssghi">
        One for the job.
        <br data-id="element-415" data-mp="2hy9o4kd7nqc7" />
        Three for the shift.
      </p>

      <div className="mt-8 grid gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1.2fr] sm:items-center" data-id="element-416" data-mp="2cy53ci59s9g6">
        {['X1 #1', 'X1 #2', 'X1 #3'].map((unit, index) => <React.Fragment key={unit} data-id="element-417" data-mp="sgw8hvpsx2e5" data-mpi={index}>
            <div className="flex items-center gap-3 border border-white/12 bg-ink-900 px-4 py-4" data-id="element-418" data-mp="xexqsefekxok">
              <BatteryFullIcon className="h-7 w-7 shrink-0 text-grit-500" aria-hidden="true" data-id="element-419" data-mp="iqks39oxl0qd" />
              <span className="font-display text-xl font-bold uppercase text-offwhite" data-id="element-420" data-mp="npe6d4c1kvrl">{unit}</span>
            </div>
            <span aria-hidden="true" className="hidden text-center font-display text-2xl font-extrabold text-steel-500 sm:block" data-id="element-421" data-mp="9p3djvr6973s">
              {index < 2 ? '+' : '='}
            </span>
          </React.Fragment>)}
        <div className="bg-grit-500 px-4 py-4 text-center font-display text-xl font-extrabold uppercase leading-tight text-ink-950" data-id="element-422" data-mp="3vyxjgdqket8j">
          Up to 10 hours of combined light*
        </div>
      </div>

      <p className="mt-6 font-display text-xl font-bold uppercase tracking-wide text-steel-200" data-id="element-423" data-mp="1u3n925uxzsb2">
        Use one. Swap when needed. Keep working.
      </p>
      <p className="mt-3 text-[11px] leading-relaxed text-steel-500" data-id="element-424" data-mp="1xo5i7j3ort8s">
        *Up to approximately 10 hours of combined runtime when three X1 units are used sequentially. Runtime varies
        depending on light mode and usage.
      </p>
    </>;
}
function VisibilityFunnel() {
  const rows = [{
    label: 'Shop light overhead',
    body: 'The light stays where the lamp is, not where your head goes.'
  }, {
    label: 'Handheld flashlight',
    body: 'One hand is gone the moment you pick it up.'
  }, {
    label: 'GritVue X1',
    body: '800 lumens travel with your line of sight while both hands stay on the job.',
    highlight: true
  }];
  return <>
      <h2 className={headlineClass} data-id="element-425" data-mp="21pim3nn7xmd2">
        The light should be
        <br data-id="element-426" data-mp="21n4u5nnu4q9q" />
        <span className="text-grit-500" data-id="element-427" data-mp="2z439w6kk298g">where your eyes are.</span>
      </h2>
      <p className={subClass} data-id="element-428" data-mp="2z6fyh2ktjhuh">
        800 lumens mounted in the frame, pointed at whatever you are looking at. Under a hood, inside a cabinet,
        above a ceiling, inside machinery, in a crawlspace you can barely turn around in.
      </p>

      <ul className="mt-8 grid gap-3 lg:grid-cols-3" data-id="element-429" data-mp="36n47kb8od8nw">
        {rows.map(row => <li key={row.label} className={`border p-5 ${row.highlight ? 'border-grit-500 bg-grit-500/10' : 'border-white/12 bg-ink-900'}`} data-id="element-430" data-mp="3bmfqnqwikq8n">
            <EyeIcon className={`h-7 w-7 ${row.highlight ? 'text-grit-500' : 'text-steel-500'}`} aria-hidden="true" data-id="element-431" data-mp="3gjpej2498tud" />
            <p className="mt-3 font-display text-xl font-bold uppercase leading-tight text-offwhite" data-id="element-432" data-mp="3plfckqsn6r14">{row.label}</p>
            <p className="mt-1.5 text-[14px] leading-relaxed text-steel-300" data-id="element-433" data-mp="3ue8ihhzuk5es">{row.body}</p>
          </li>)}
      </ul>

      <p className={statementClass} data-id="element-434" data-mp="868mlja6sqzc">
        Both hands on the job.
        <br data-id="element-435" data-mp="88393213wr5j" />
        Light on the task.
      </p>
    </>;
}
function FoggingFunnel() {
  const included = [{
    name: 'GritVue anti-fog',
    tag: 'Included free'
  }, {
    name: 'GritVue hard case',
    tag: 'Included free'
  }, {
    name: 'Lifetime Replacement Warranty',
    tag: '$19.95 value · Free on multi-packs'
  }];
  return <>
      <h2 className={headlineClass} data-id="element-436" data-mp="17gq1h9wax2b0">
        Sweat. Heat. Humidity.
        <br data-id="element-437" data-mp="17evrgqmkiulp" />
        <span className="text-grit-500" data-id="element-438" data-mp="17elyqva529cb">Real work happens in the real world.</span>
      </h2>
      <p className={subClass} data-id="element-439" data-mp="12fsodbq5eygd">
        Every pair of work glasses fogs when the temperature swings. That is why your X1 ships with GritVue anti-fog
        in the box, not as something we sell you later. Eyewear should arrive ready for the conditions you work in.
      </p>

      <div className="mt-8 grid gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-center" data-id="element-440" data-mp="ojosldzmmi1s">
        {/* Placeholder until the branded anti-fog render is supplied. */}
        <div className="flex aspect-[4/3] items-center justify-center border border-white/12 bg-ink-900" data-id="element-441" data-mp="3s6p3z3ukuj68">
          <DropletsIcon className="h-16 w-16 text-grit-500" aria-hidden="true" data-id="element-442" data-mp="3n8w04hgqy194" />
        </div>
        <ul className="grid gap-3" data-id="element-443" data-mp="22ptmxihaylj9">
          {included.map(item => <li key={item.name} className="flex items-center justify-between gap-4 border border-white/12 bg-ink-900 px-5 py-4" data-id="element-444" data-mp="27gs5yptodxq7">
              <span className="font-display text-xl font-bold uppercase leading-tight text-offwhite" data-id="element-445" data-mp="1tr855pjnnajj">
                {item.name}
              </span>
              <span className="shrink-0 bg-grit-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-ink-950" data-id="element-446" data-mp="2mdsyuwnlryq9">
                {item.tag}
              </span>
            </li>)}
        </ul>
      </div>

      <p className={statementClass} data-id="element-447" data-mp="2cfd6kqcuajf6">Your X1 comes work-ready.</p>
    </>;
}
function LensFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-448" data-mp="2hh2kcdrg5b6y">
        One frame.
        <br data-id="element-449" data-mp="10zojlyzl5ked" />
        <span className="text-grit-500" data-id="element-450" data-mp="10zyd3wjua9ot">Three ways to work.</span>
      </h2>
      <p className={subClass} data-id="element-451" data-mp="1602stkcxb0dz">
        Every X1 arrives with all three lenses. They pop out tool-free, so you change the tint instead of changing
        the glasses. Need your prescription? The GritVue X1 RX Insert™ is sold separately.
      </p>

      <ul className="mt-8 grid gap-3 sm:grid-cols-3" data-id="element-452" data-mp="3l8q65z8uhotr">
        {lenses.map(lens => <li key={lens.name} className="border border-white/12 bg-ink-900 p-5" data-id="element-453" data-mp="36hhap9i9h98p">
            <span className="block h-10 w-10 rounded-full border border-white/20" style={{
          backgroundColor: lens.swatch
        }} aria-hidden="true" data-id="element-454" data-mp="3bfj3im5homxd" />
            <p className="mt-4 font-display text-2xl font-bold uppercase leading-none text-offwhite" data-id="element-455" data-mp="31jfq2k56t1v4">{lens.name}</p>
            <p className="mt-2 text-[14px] leading-relaxed text-steel-300" data-id="element-456" data-mp="2nkpu57u3t88c">{lens.use}</p>
          </li>)}
      </ul>

      <p className={statementClass} data-id="element-457" data-mp="2sjsysr669cfs">
        Replace the lens.
        <br data-id="element-458" data-mp="ls1o6qq2s6ov" />
        Not the system.
      </p>
      <p className="mt-6 font-display text-xl font-bold uppercase tracking-wide text-steel-200" data-id="element-459" data-mp="qqle127si2mi">
        1 X1 = 3 lenses · Buy 2, get 1 free = 9 lenses · Buy 4, get 2 free = 18 lenses
      </p>
    </>;
}
function DurabilityFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-460" data-mp="26b443xgwm0if">
        Truck. Toolbag.
        <br data-id="element-461" data-mp="2kyp4vkqb3nn6" />
        <span className="text-grit-500" data-id="element-462" data-mp="2kzidkr8tsn7y">Jobsite. Repeat.</span>
      </h2>
      <p className={subClass} data-id="element-463" data-mp="2g80anxn8sckc">
        The X1 lives in a hard case in your bag and gets pulled out with greasy gloves ten times a day. If it breaks,
        cracks or a lens gets damaged, we replace it free for as long as you own it under our Lifetime Limited
        Warranty. One email is all it takes.
      </p>

      <div className="mt-8 flex flex-col items-start gap-6 border border-grit-500/50 bg-ink-900 p-6 sm:flex-row sm:items-center sm:p-8" data-id="element-464" data-mp="33xkz2w260210">
        <LifetimeSeal className="h-24 w-24 shrink-0" data-id="element-465" data-mp="2yyzuyljsky7a" />
        <div data-id="element-466" data-mp="19iicp78ex02b">
          <p className="font-display text-2xl font-bold uppercase leading-tight text-offwhite sm:text-3xl" data-id="element-467" data-mp="19kbjqnmxtmau">
            Backed by our Lifetime Replacement Warranty.
          </p>
          <p className="mt-2 text-[15px] leading-relaxed text-steel-300" data-id="element-468" data-mp="1u8ul99hfbier">
            A $19.95 value, included free on every X1 in a multi-pack.
          </p>
          <Link to="/legal/guarantee" className="mt-3 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-grit-500 underline-offset-4 transition-colors duration-150 ease-grit hover:text-grit-400 hover:underline" data-id="element-469" data-mp="1yc0sajcd1dma">
            View Lifetime Replacement Warranty terms
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-470" data-mp="2h8abfl24cud5" />
          </Link>
        </div>
      </div>

      <p className={statementClass} data-id="element-471" data-mp="2lz8wexy1ygmq">
        We built the X1 to be used.
        <br data-id="element-472" data-mp="2qyc1ujhy2ov8" />
        Not babied.
      </p>
    </>;
}
function PriceFunnel({
  priceUnlocked
}: {
  priceUnlocked: boolean;
}) {
  const splitRef = useTrackOnView<HTMLDivElement>('price_split_offer_viewed');
  const rescueRef = useTrackOnView<HTMLDivElement>('rescue_20_viewed', priceUnlocked);
  return <>
      <h2 className={headlineClass} data-id="element-473" data-mp="p9z586obp621">Don&apos;t want to spend that much?</h2>
      <p className="mt-5 font-display text-2xl font-bold uppercase tracking-wide text-grit-500 sm:text-3xl" data-id="element-474" data-mp="p739mozy6tgr">
        You don&apos;t have to keep all three.
      </p>
      <p className="mt-6 border-l-4 border-grit-500 pl-4 font-display text-[clamp(1.75rem,5vw,3rem)] font-extrabold uppercase leading-tight text-offwhite" data-id="element-475" data-mp="fbrzyl3eccfv">
        Keep one.
        <br data-id="element-476" data-mp="17yvyq8bwbuj7" />
        Split the rest with your crew.
      </p>

      <div ref={splitRef} className="mt-10 grid gap-6 lg:grid-cols-2" data-id="element-477" data-mp="130uevl6nbr6o">
        <div className="border border-white/12 bg-ink-900 p-6 sm:p-8" data-id="element-478" data-mp="131magw08towy">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-479" data-mp="y8t8gfw5suok">Buy 2, get 1 free</p>
          <p className="mt-1 font-display text-2xl font-bold uppercase text-offwhite" data-id="element-480" data-mp="tahz069s0m6v">3 X1 complete systems</p>
          <p className="mt-2 font-display text-5xl font-extrabold leading-none text-offwhite" data-id="element-481" data-mp="1qrq6oib3qmdx">$99.90</p>
          <ul className="mt-6 grid gap-2 border-t border-white/10 pt-5" data-id="element-482" data-mp="1lxlwlq6jhy8m">
            {['You: 1 X1', 'Coworker: 1 X1', 'Coworker: 1 X1'].map(line => <li key={line} className="flex items-center gap-2.5 text-[15px] font-semibold text-offwhite" data-id="element-483" data-mp="1bh1uacp7ar3c">
                <CheckIcon className="h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-484" data-mp="wkkl9s4hhojy" />
                {line}
              </li>)}
          </ul>
          <p className="mt-4 bg-grit-500 px-4 py-3 font-display text-2xl font-extrabold uppercase leading-none text-ink-950 sm:text-3xl" data-id="element-485" data-mp="1p86mitq8oh3n">
            $33.30 each
          </p>
        </div>

        <div className="border border-white/12 bg-ink-900 p-6 sm:p-8" data-id="element-486" data-mp="1u9487gagjt9w">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-487" data-mp="1fi3lc68yrwu8">
            Your coworkers may want their own too
          </p>
          <dl className="mt-4 grid gap-2.5 text-[15px]" data-id="element-488" data-mp="286iy0m4mi32e">
            <div className="flex items-baseline justify-between gap-4" data-id="element-489" data-mp="284y5t35s6w7p">
              <dt className="text-steel-300" data-id="element-490" data-mp="2cyj2mrvn1k5b">Coworker #1</dt>
              <dd className="font-display text-2xl font-extrabold text-offwhite" data-id="element-491" data-mp="1i8ut8b0d9de">$49.95</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4" data-id="element-492" data-mp="3spv53js8ga38">
              <dt className="text-steel-300" data-id="element-493" data-mp="b8lnjnsz88um">Coworker #2</dt>
              <dd className="font-display text-2xl font-extrabold text-offwhite" data-id="element-494" data-mp="b828ceupnyka">$49.95</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 border-t border-white/10 pt-3" data-id="element-495" data-mp="6aacxauuxubc">
              <dt className="font-semibold text-offwhite" data-id="element-496" data-mp="3eren2wox6x5s">Total</dt>
              <dd className="font-display text-3xl font-extrabold text-grit-500" data-id="element-497" data-mp="39riv5vthyw6l">$99.90</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4" data-id="element-498" data-mp="3nrjvna5uo1lj">
              <dt className="text-steel-300" data-id="element-499" data-mp="3jo5dacufv94x">Pack cost</dt>
              <dd className="font-display text-2xl font-extrabold text-offwhite" data-id="element-500" data-mp="3jqrk2a9i9km3">$99.90</dd>
            </div>
          </dl>
          <p className="mt-5 font-display text-2xl font-bold uppercase leading-tight text-offwhite" data-id="element-501" data-mp="yyfy1yptplbp">
            Your pack could be fully covered.*
            <span className="mt-1 block text-grit-500" data-id="element-502" data-mp="3rs4w41zo2mne">Your own X1 could effectively cost you $0.*</span>
          </p>
          <p className="mt-3 text-[11px] leading-relaxed text-steel-500" data-id="element-503" data-mp="og469x749ge">
            *Only if two coworkers voluntarily reimburse you at that amount. GritVue does not guarantee resale,
            coworker participation, reimbursement or profit. Taxes and fees may vary.
          </p>
        </div>
      </div>

      <CtaButton offer="trio" className="mt-8 w-full sm:w-auto" onClick={() => {
      trackCustom('price_split_clicked');
      trackCustom('mini_funnel_cta_clicked');
    }} data-id="element-504" data-mp="390c2c5crwxem">
        Get 3 X1 Systems — $99.90
      </CtaButton>

      {priceUnlocked && <div ref={rescueRef} className="mt-12 border-2 border-grit-500 bg-ink-900" data-id="element-505" data-mp="3mvebd1cx32vn">
          <p className="bg-grit-500 px-5 py-2.5 text-[11px] font-extrabold uppercase tracking-[0.22em] text-ink-950 sm:px-7" data-id="element-506" data-mp="2q9ktd4wp790r">
            Still just want one?
          </p>
          <div className="grid gap-8 p-5 sm:p-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center" data-id="element-507" data-mp="2msg9by7lxksj">
            <div data-id="element-508" data-mp="2hvy99bn82amj">
              <p className="font-display text-xl font-bold uppercase tracking-wide text-steel-200" data-id="element-509" data-mp="2hvg41pil0m2h">
                We would rather let you try GritVue than watch you leave.
              </p>
              <h3 className="grit-headline mt-3 text-[clamp(2rem,6vw,3.5rem)] font-extrabold uppercase leading-[0.95] text-offwhite" data-id="element-510" data-mp="28tglgsrx59xf">
                Here&apos;s <span className="text-grit-500" data-id="element-511" data-mp="1z4f71710v0lk">20% off</span> your first single X1.
              </h3>
              <p className="mt-4 text-[15px] leading-relaxed text-steel-300 sm:text-base" data-id="element-512" data-mp="1u4ssmu9gjeb6">
                One X1 only. Not valid on Buy 2 Get 1 Free, Buy 4 Get 2 Free or the Business / Crew Pack, and it
                cannot be combined with bundle pricing or other discount codes. One use per customer.
              </p>
              <Bullets items={['1 GritVue X1 complete system', '3 interchangeable lenses: Clear, Amber, Smoke', 'Hard case, pouch, cloth, USB cable and anti-fog spray', '1-year replacement coverage']} data-id="element-513" data-mp="1g6cdcexj4fhf" />
            </div>

            <div className="border border-white/12 bg-ink-950 p-5 sm:p-7" data-id="element-514" data-mp="2v3f0o0yekfcm">
              <p className="text-base text-steel-400 line-through" data-id="element-515" data-mp="2zyubsl50zx5c">$49.95</p>
              <p className="font-display text-[clamp(3rem,11vw,5rem)] font-extrabold leading-none text-offwhite" data-id="element-516" data-mp="2lagd8men6uul">
                $39.96
              </p>
              <p className="mt-3 inline-flex bg-grit-500 px-3 py-1.5 font-display text-xl font-extrabold uppercase leading-none text-ink-950" data-id="element-517" data-mp="2q42ihtih7wgd">
                20% off
              </p>
              <a href="#offer" data-buy-intent="true" onClick={() => {
            requestOffer('single');
            trackCustom('rescue_20_clicked');
            trackCustom('mini_funnel_cta_clicked');
            trackCustom(packEvents.single.addToCart);
          }} className="mt-6 flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] sm:text-2xl" data-id="element-518" data-mp="2gabusqu6bq1q">
                Try 1 X1 — $39.96
                <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-519" data-mp="26mpp1dagbwo0" />
              </a>
              <p className="mt-3 text-center text-[13px] text-steel-400" data-id="element-520" data-mp="21pfvblg14yud">Single X1 only. No discount stacking.</p>
            </div>
          </div>
        </div>}
    </>;
}
const closingCta: Record<ObjectionId, string> = {
  battery: 'Get 3 X1 Systems — $99.90',
  visibility: 'Get 3 X1 Systems — $99.90',
  fogging: 'Get your GritVue X1',
  lenses: 'Choose your X1 kit',
  durability: 'Try GritVue X1',
  price: 'Get 3 X1 Systems — $99.90'
};

/**
 * One dynamic recovery area, hidden until the visitor tells us what is holding
 * them back. Only the matching funnel renders, so the normal page stays clean.
 */
export function ObjectionRecovery({
  active,
  priceUnlocked,
  onChooseAnother
}: ObjectionRecoveryProps) {
  const choice = active ? objectionChoices.find(item => item.id === active) : undefined;
  useEffect(() => {
    if (choice) trackCustom(choice.viewEvent);
  }, [choice]);
  return <AnimatePresence initial={false} data-id="element-521" data-mp="2e6d057bml7jr">
      {choice && <motion.section id={RECOVERY_ID} key={choice.id} aria-labelledby="recovery-heading" className="scroll-mt-28 border-b border-white/10 bg-ink-950" initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 12
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-522" data-mp="2a7voqbl69awj">
          <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-523" data-mp="1qhtqancxgmpk">
            <p className="inline-flex flex-wrap items-center gap-2 border border-grit-500/60 bg-grit-500/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-grit-500" data-id="element-524" data-mp="60avplmm3u4n">
              You said this matters most to you:
              <span className="text-offwhite" data-id="element-525" data-mp="3t73fcvz0f1xw">{choice.label}</span>
            </p>

            <div id="recovery-heading" className="mt-6" data-id="element-526" data-mp="200ne2bckrmm">
              {choice.id === 'battery' && <BatteryFunnel data-id="element-527" data-mp="pojw2iyb76f8" />}
              {choice.id === 'visibility' && <VisibilityFunnel data-id="element-528" data-mp="tqev1gsrp7pu" />}
              {choice.id === 'fogging' && <FoggingFunnel data-id="element-529" data-mp="tpvdi1714lcc" />}
              {choice.id === 'lenses' && <LensFunnel data-id="element-530" data-mp="fr5h6ns1app1" />}
              {choice.id === 'durability' && <DurabilityFunnel data-id="element-531" data-mp="fsq9e6qvlwjq" />}
              {choice.id === 'price' && <PriceFunnel priceUnlocked={priceUnlocked} data-id="element-532" data-mp="konns7ikwstv" />}
            </div>

            {choice.id !== 'price' && <PackClose recommended={choice.recommend} cta={closingCta[choice.id]} data-id="element-533" data-mp="30fa1hb6fasjv" />}

            <button type="button" onClick={onChooseAnother} className="mt-8 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-steel-400 transition-colors duration-150 ease-grit hover:text-grit-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-534" data-mp="35h92wfilrfe6">
              <RefreshCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-535" data-mp="27n84338yz113" />
              Not your main concern? Choose another
            </button>
          </div>
        </motion.section>}
    </AnimatePresence>;
}